/**
 * Validation and scoring utilities for question answers
 */

import type { DragDropAnswerConfig, MatchingAnswerConfig, FillInTheBlanksAnswerConfig, PaintingAnswerConfig, AnswerKeyValue } from '../../../editor/types';
import { getT } from "../../../locales";
import { deriveBlanksFromContent } from "./text";

export interface ValidationResult {
  isCorrect: boolean;
  score: number;
  maxScore: number;
  feedback: string;
  elementResults: ElementValidationResult[];
}

export interface ElementValidationResult {
  elementId: string;
  elementType: string;
  userAnswer: string | string[];
  correctAnswers: string[];
  isCorrect: boolean;
  partialCredit: number;
}

export interface QuestionData {
  question: {
    answerKey: Record<string, string[]>;
    points: number;
  };
  validation: {
    autoValidate: boolean;
    interactiveElements: Array<{
      id: string;
      type: string;
      required: boolean;
      correctAnswers: string[];
    }>;
    scoring: {
      totalPoints: number;
      method: 'all-or-nothing' | 'partial' | 'per-element';
      passingScore: number;
    };
  };
}

export interface UserAnswers {
  [elementId: string]: string | string[];
}

/**
 * Validates user answers against the answer key
 * @param questionData - The exported question JSON data
 * @param userAnswers - User's answers mapped by element ID
 * @returns Validation result with score and feedback
 */
export function validateAnswers(
  questionData: QuestionData,
  userAnswers: UserAnswers
): ValidationResult {
  const { validation } = questionData;
  const elementResults: ElementValidationResult[] = [];

  // Validate each interactive element
  for (const element of validation.interactiveElements) {
    const userAnswer = userAnswers[element.id];
    const correctAnswers = element.correctAnswers || [];

    // Check if user provided an answer
    if (element.required && !userAnswer) {
      elementResults.push({
        elementId: element.id,
        elementType: element.type,
        userAnswer: [],
        correctAnswers,
        isCorrect: false,
        partialCredit: 0,
      });
      continue;
    }

    // Normalize answers to arrays for comparison
    const userAnswerArray = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
    const isCorrect = areAnswersEqual(userAnswerArray, correctAnswers);

    // Calculate partial credit for partial matches
    let partialCredit = 0;
    if (!isCorrect && validation.scoring.method === 'partial') {
      partialCredit = calculatePartialCredit(userAnswerArray, correctAnswers);
    }

    elementResults.push({
      elementId: element.id,
      elementType: element.type,
      userAnswer,
      correctAnswers,
      isCorrect,
      partialCredit: isCorrect ? 1 : partialCredit,
    });
  }

  // Calculate total score based on scoring method
  const score = calculateScore(elementResults, validation.scoring);
  const maxScore = validation.scoring.totalPoints;
  const passed = score >= validation.scoring.passingScore;

  // Generate feedback
  const correctCount = elementResults.filter(r => r.isCorrect).length;
  const totalCount = elementResults.length;
  const feedback = generateFeedback(correctCount, totalCount, score, maxScore, passed);

  return {
    isCorrect: correctCount === totalCount,
    score,
    maxScore,
    feedback,
    elementResults,
  };
}

/**
 * Compares two answer arrays for equality (order-independent for multi-select)
 */
function areAnswersEqual(userAnswers: string[], correctAnswers: string[]): boolean {
  if (userAnswers.length !== correctAnswers.length) {
    return false;
  }

  const sortedUser = [...userAnswers].sort();
  const sortedCorrect = [...correctAnswers].sort();

  return sortedUser.every((answer, index) => answer === sortedCorrect[index]);
}

/**
 * Calculates partial credit for partially correct answers
 */
function calculatePartialCredit(userAnswers: string[], correctAnswers: string[]): number {
  if (correctAnswers.length === 0) return 0;

  const correctCount = userAnswers.filter(answer =>
    correctAnswers.includes(answer)
  ).length;

  const incorrectCount = userAnswers.filter(answer =>
    !correctAnswers.includes(answer)
  ).length;

  // Partial credit: (correct selections - incorrect selections) / total correct answers
  // Minimum 0, maximum 1
  const credit = (correctCount - incorrectCount) / correctAnswers.length;
  return Math.max(0, Math.min(1, credit));
}

/**
 * Calculates total score based on element results and scoring method
 */
function calculateScore(
  results: ElementValidationResult[],
  scoring: QuestionData['validation']['scoring']
): number {
  if (!scoring) return 0;

  const { totalPoints, method } = scoring;

  switch (method) {
    case 'all-or-nothing':
      // All questions must be correct to get any points
      return results.every(r => r.isCorrect) ? totalPoints : 0;

    case 'partial':
      // Sum partial credit across all elements
      const totalCredit = results.reduce((sum, r) => sum + r.partialCredit, 0);
      return (totalCredit / results.length) * totalPoints;

    case 'per-element':
      // Each element worth equal points
      const pointsPerElement = totalPoints / results.length;
      return results.reduce((sum, r) => {
        return sum + (r.isCorrect ? pointsPerElement : r.partialCredit * pointsPerElement);
      }, 0);

    default:
      return 0;
  }
}

/**
 * Generates human-readable feedback
 */
function generateFeedback(
  correctCount: number,
  totalCount: number,
  score: number,
  maxScore: number,
  passed: boolean
): string {
  const t = getT();
  const percentage = Math.round((score / maxScore) * 100);

  if (correctCount === totalCount) {
    return `${t('validation.feedback.perfect')} ${t('validation.score').replace('{score}', String(score)).replace('{maxScore}', String(maxScore)).replace('{percentage}', String(percentage))}`;
  }

  if (passed) {
    return `${t('validation.feedback.success')} ${t('validation.correctCountSummary').replace('{correctCount}', String(correctCount)).replace('{totalCount}', String(totalCount))} ${t('validation.score').replace('{score}', String(score)).replace('{maxScore}', String(maxScore)).replace('{percentage}', String(percentage))}`;
  }

  return `${t('validation.correctCountSummary').replace('{correctCount}', String(correctCount)).replace('{totalCount}', String(totalCount))} ${t('validation.score').replace('{score}', String(score)).replace('{maxScore}', String(maxScore)).replace('{percentage}', String(percentage))}. ${t('validation.feedback.fail')}`;
}

/**
 * Validates a single element answer
 */
export function validateElementAnswer(
  elementId: string,
  userAnswer: string | string[],
  correctAnswers: string[]
): boolean {
  const userAnswerArray = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
  return areAnswersEqual(userAnswerArray, correctAnswers);
}

/**
 * Gets detailed feedback for a specific element
 */
export function getElementFeedback(
  elementResult: ElementValidationResult
): string {
  const t = getT();

  if (elementResult.isCorrect) {
    return t('validation.result.correct');
  }

  if (elementResult.partialCredit > 0) {
    const percentage = Math.round(elementResult.partialCredit * 100);
    return `${t('validation.result.partiallyCorrect')} (%${percentage} ${t('validation.partialCreditSuffix')})`;
  }

  return t('validation.result.wrong');
}

/**
 * Validates canvas requirements before saving
 * - Must have at least one element
 */
export interface CanvasValidationResult {
  isValid: boolean;
  message: string;
}

export function validateCanvasRequirements(elements: Array<{ type: string }>): CanvasValidationResult {
  const t = getT();

  if (elements.length === 0) {
    return {
      isValid: false,
      message: t('validation.canvas.noElements')
    };
  }

  return {
    isValid: true,
    message: ''
  };
}

/**
 * Validates answer key for normal (question) save
 * - If form elements (choice) exist, answer key must be filled
 * - If drop-target elements exist, drag-drop answer key must be filled
 * - If order-list elements exist, order answer key must be filled
 * - If matching-point elements exist, matching answer key must be filled
 */
export function validateAnswerKey(
  elements: Array<{ id: string; type: string; data?: Record<string, unknown> }>,
  answerKey?: Record<string, AnswerKeyValue>
): CanvasValidationResult {
  const t = getT();
  const formElements = elements.filter(element => element.type === 'choice');
  const dropTargets = elements.filter(element => element.type === 'drop-target');
  const orderLists = elements.filter(element => element.type === 'order-list');
  const matchingPoints = elements.filter(element => element.type === 'matching-point');
  const fillInTheBlanksElements = elements.filter(element => element.type === 'fill-in-the-blanks');
  const sourcePoints = matchingPoints.filter(element => {
    const data = element.data as { pointType?: 'source' | 'target' } | undefined;
    return data?.pointType === 'source';
  });

  // Check choice elements
  if (formElements.length > 0) {
    const missingAnswerKeys = formElements.filter(element => {
      const answers = answerKey?.[element.id];
      return !answers || !Array.isArray(answers) || answers.length === 0;
    });

    if (missingAnswerKeys.length > 0) {
      return {
        isValid: false,
        message: t('validation.choice.noAnswerKey')
      };
    }
  }

  // Check drop-target elements
  if (dropTargets.length > 0) {
    const missingDragDropKeys = dropTargets.filter(element => {
      const config = answerKey?.[element.id];
      if (!config || Array.isArray(config)) {
        return true;
      }
      // Type guard: check if it's DragDropAnswerConfig
      if ('acceptedItems' in config) {
        return config.acceptedItems.length === 0;
      }
      // If it's MatchingAnswerConfig, it's not valid for drop-target
      return true;
    });

    if (missingDragDropKeys.length > 0) {
      return {
        isValid: false,
        message: t('validation.dragDrop.noAnswerKey')
      };
    }
  }

  // Check order-list elements
  if (orderLists.length > 0) {
    const missingOrderListKeys = orderLists.filter(element => {
      const order = answerKey?.[element.id];
      return !order || !Array.isArray(order) || order.length === 0;
    });

    if (missingOrderListKeys.length > 0) {
      return {
        isValid: false,
        message: t('validation.orderList.noAnswerKey')
      };
    }
  }

  // Check matching-point elements
  if (sourcePoints.length > 0) {
    // Find matching answer key
    const matchingAnswerKey = answerKey ? Object.entries(answerKey).find(([key, value]) => {
      return (
        key === 'matching-connections' ||
        (!Array.isArray(value) && !('acceptedItems' in value) && 'connections' in value)
      );
    }) : undefined;

    if (!matchingAnswerKey) {
      return {
        isValid: false,
        message: t('validation.matching.noAnswerKey')
      };
    }

    const [, matchingConfig] = matchingAnswerKey;
    if (!matchingConfig || Array.isArray(matchingConfig) || !('connections' in matchingConfig)) {
      return {
        isValid: false,
        message: t('validation.matching.noAnswerKey')
      };
    }

    const connections = (matchingConfig as MatchingAnswerConfig).connections;
    if (!connections || connections.length === 0) {
      return {
        isValid: false,
        message: t('validation.matching.noConnections')
      };
    }
  }

  // Check fill-in-the-blanks elements
  if (fillInTheBlanksElements.length > 0) {
    const missingFillInTheBlanksKeys = fillInTheBlanksElements.filter(element => {
      const config = answerKey?.[element.id];
      if (!config || Array.isArray(config)) {
        return true;
      }
      // Type guard: check if it's FillInTheBlanksAnswerConfig
      if ('blanks' in config) {
        const blanksConfig = config as FillInTheBlanksAnswerConfig;
        return !blanksConfig.blanks || blanksConfig.blanks.length === 0;
      }
      // If it's not FillInTheBlanksAnswerConfig, it's not valid
      return true;
    });

    if (missingFillInTheBlanksKeys.length > 0) {
      return {
        isValid: false,
        message: t('validation.fillInTheBlanks.noAnswerKey')
      };
    }
  }

  // Check paint-area elements (optional - painting answer keys are optional)
  const paintAreas = elements.filter(element => element.type === 'paint-area');
  if (paintAreas.length > 0) {
    const missingPaintKeys = paintAreas.filter(element => {
      const config = answerKey?.[element.id];
      if (!config || Array.isArray(config)) {
        return true;
      }
      if ('paintSwatchId' in config || 'paintColor' in config) {
        const paintingConfig = config as {
          paintSwatchId?: string;
          paintColor?: string;
        };
        const hasSwatch =
          !!paintingConfig.paintSwatchId &&
          String(paintingConfig.paintSwatchId).trim() !== '';
        const hasColor =
          !!paintingConfig.paintColor &&
          String(paintingConfig.paintColor).trim() !== '';
        return !hasSwatch && !hasColor;
      }
      return true;
    });

    if (missingPaintKeys.length > 0) {
      return {
        isValid: false,
        message: t('validation.painting.noAnswerKey')
      };
    }
  }

  return {
    isValid: true,
    message: ''
  };
}

/**
 * Validates drag-drop answers against the answer key
 * @param targetId - The drop target element ID
 * @param userItems - Array of item IDs dropped by the user (can include instance IDs like "id::instance::xxx")
 * @param config - The answer configuration for this target
 * @returns true if the answer is correct
 */
export function validateDragDropAnswer(
  _targetId: string,
  userItems: string[],
  config: DragDropAnswerConfig
): { isCorrect: boolean; partialCredit: number } {
  // Extract base IDs from instance IDs (e.g., "item1::instance::123" -> "item1")
  const userBaseIds = userItems.map(item => {
    const parts = item.split('::instance::');
    return parts[0];
  });

  const { acceptedItems, acceptedCounts } = config;

  // Kabul edilen öğeleri, adetlerine göre çoklu-kümeye (multiset) genişlet.
  // acceptedCounts varsa her öğe o kadar tekrar eder → tam adet eşleşmesi.
  const requiredItems = acceptedCounts
    ? acceptedItems.flatMap((id) =>
        Array(Math.max(0, Math.floor(acceptedCounts[id] ?? 1))).fill(id)
      )
    : acceptedItems;

  // Sıra önemsiz — tam adet eşleşmesi (multiset karşılaştırması).
  const isCorrect = areAnswersEqual(userBaseIds, requiredItems);

  // Calculate partial credit
  if (!isCorrect && requiredItems.length > 0) {
    const correctCount = userBaseIds.filter(id => requiredItems.includes(id)).length;
    const incorrectCount = userBaseIds.filter(id => !requiredItems.includes(id)).length;
    const partialCredit = Math.max(0, (correctCount - incorrectCount) / requiredItems.length);
    return { isCorrect: false, partialCredit };
  }

  return { isCorrect, partialCredit: isCorrect ? 1 : 0 };
}

/**
 * Gets detailed feedback for a drag-drop element
 */
export function getDragDropElementFeedback(
  isCorrect: boolean,
  partialCredit: number,
  _config: DragDropAnswerConfig,
  _userItemCount: number
): string {
  const t = getT();

  if (isCorrect) {
    return t('validation.dragDrop.correctResult');
  }

  if (partialCredit > 0) {
    const percentage = Math.round(partialCredit * 100);
    return `${t('validation.result.partiallyCorrect')} (%${percentage} ${t('validation.partialCreditSuffix')})`;
  }

  return t('validation.dragDrop.wrongItems');
}

/**
 * Validates order-list answers against the answer key
 * @param orderListId - The order-list element ID
 * @param userOrder - Array of item IDs in the order provided by the user
 * @param correctOrder - Array of item IDs in the correct order from answer key
 * @returns true if the order matches exactly
 */
export function validateOrderListAnswer(
  _orderListId: string,
  userOrder: string[],
  correctOrder: string[]
): { isCorrect: boolean; partialCredit: number } {
  // Order must match exactly for order-list
  if (userOrder.length !== correctOrder.length) {
    return { isCorrect: false, partialCredit: 0 };
  }

  const isCorrect = userOrder.every((id, index) => id === correctOrder[index]);
  
  // Calculate partial credit based on items in correct position
  if (!isCorrect && correctOrder.length > 0) {
    const correctPositions = userOrder.filter((id, index) => id === correctOrder[index]).length;
    const partialCredit = correctPositions / correctOrder.length;
    return { isCorrect: false, partialCredit };
  }

  return { isCorrect, partialCredit: isCorrect ? 1 : 0 };
}

/**
 * Gets detailed feedback for an order-list element
 */
export function getOrderListElementFeedback(
  isCorrect: boolean,
  partialCredit: number,
  userItemCount: number,
  correctItemCount: number
): string {
  const t = getT();

  if (isCorrect) {
    return t('validation.orderList.correct');
  }

  if (userItemCount !== correctItemCount) {
    return t('validation.orderList.wrongItemCount').replace('{correct}', String(correctItemCount)).replace('{current}', String(userItemCount));
  }

  if (partialCredit > 0) {
    const percentage = Math.round(partialCredit * 100);
    return `${t('validation.result.partiallyCorrect')} (%${percentage} ${t('validation.partialCreditSuffix')}) - ${t('validation.partialPositionHint')}`;
  }

  return t('validation.orderList.wrongOrder');
}

/**
 * Per-element result of a preview answer check.
 */
export interface PreviewCheckItem {
  elementId: string;
  elementType: string;
  answered: boolean;
  isCorrect: boolean;
}

/**
 * Aggregate result of checking all gradable answers in preview mode.
 */
export interface PreviewCheckResult {
  hasGradable: boolean;
  total: number;
  correct: number;
  allCorrect: boolean;
  items: PreviewCheckItem[];
}

const normalizeBlankText = (value: string): string =>
  (value || '').trim().toLocaleLowerCase('tr');

/**
 * Finds the matching answer config in the answer key. The matching answer key
 * may be stored under the literal "matching-connections" key or under a source
 * point's element id.
 */
function findMatchingAnswerConfig(
  answerKey: Record<string, AnswerKeyValue>
): MatchingAnswerConfig | undefined {
  const entry = Object.entries(answerKey).find(([key, value]) => {
    return (
      key === 'matching-connections' ||
      (!Array.isArray(value) &&
        !('acceptedItems' in (value as object)) &&
        'connections' in (value as object))
    );
  });
  if (!entry) return undefined;
  const [, config] = entry;
  if (!config || Array.isArray(config) || !('connections' in config)) {
    return undefined;
  }
  return config as MatchingAnswerConfig;
}

/**
 * Checks the user's entered answers (from preview mode) against the answer key.
 * Covers choice, drop-target, order-list, fill-in-the-blanks, paint-area and
 * matching-point elements. Matching is graded as a single aggregate item.
 *
 * @param elements - The question elements
 * @param answerKey - The answer key (state.canvas.question.answerKey)
 * @param formState - The user's answers captured in preview (per element id)
 * @param matchingConnections - The user's matching connections (sourceId -> targetIds)
 */
export function checkPreviewAnswers(
  elements: Array<{ id: string; type: string; data?: Record<string, unknown> }>,
  answerKey: Record<string, AnswerKeyValue> | undefined,
  formState: Record<string, string | string[] | Record<string, string>>,
  matchingConnections: Record<string, string[]>
): PreviewCheckResult {
  const items: PreviewCheckItem[] = [];
  const key = answerKey || {};

  for (const element of elements) {
    switch (element.type) {
      case 'choice': {
        const correct = key[element.id];
        if (!Array.isArray(correct)) break;
        const user = formState[element.id];
        const userArr = Array.isArray(user)
          ? user
          : typeof user === 'string' && user
          ? [user]
          : [];
        const answered = userArr.length > 0;
        const isCorrect =
          answered && validateElementAnswer(element.id, userArr, correct);
        items.push({ elementId: element.id, elementType: element.type, answered, isCorrect });
        break;
      }
      case 'drop-target': {
        const config = key[element.id];
        if (!config || Array.isArray(config) || !('acceptedItems' in config)) break;
        const user = formState[element.id];
        const userArr = Array.isArray(user) ? user : [];
        const answered = userArr.length > 0;
        const { isCorrect } = validateDragDropAnswer(
          element.id,
          userArr,
          config as DragDropAnswerConfig
        );
        items.push({
          elementId: element.id,
          elementType: element.type,
          answered,
          isCorrect: answered && isCorrect,
        });
        break;
      }
      case 'order-list': {
        const correctOrder = key[element.id];
        if (!Array.isArray(correctOrder)) break;
        const user = formState[element.id];
        const userArr = Array.isArray(user) ? user : [];
        const answered = userArr.length > 0;
        const { isCorrect } = validateOrderListAnswer(
          element.id,
          userArr,
          correctOrder
        );
        items.push({
          elementId: element.id,
          elementType: element.type,
          answered,
          isCorrect: answered && isCorrect,
        });
        break;
      }
      case 'text': {
        // A text block containing `[answer]` markers grades like
        // fill-in-the-blanks; the correct answer is the bracket content
        // (self-contained — no answer-key entry needed).
        const textData = element.data as { content?: string };
        const blanks = deriveBlanksFromContent(textData.content);
        if (blanks.length === 0) break;
        const user = formState[element.id];
        const userValues =
          user && typeof user === 'object' && !Array.isArray(user)
            ? (user as Record<string, string>)
            : {};
        const answered = Object.values(userValues).some(
          (value) => !!value && value.trim() !== ''
        );
        const isCorrect =
          answered &&
          blanks.every(
            (blank) =>
              normalizeBlankText(userValues[blank.id]) ===
              normalizeBlankText(blank.value)
          );
        items.push({
          elementId: element.id,
          elementType: element.type,
          answered,
          isCorrect,
        });
        break;
      }
      case 'fill-in-the-blanks': {
        const config = key[element.id];
        if (!config || Array.isArray(config) || !('blanks' in config)) break;
        const blanks = (config as FillInTheBlanksAnswerConfig).blanks || [];
        if (blanks.length === 0) break;
        const user = formState[element.id];
        const userValues =
          user && typeof user === 'object' && !Array.isArray(user)
            ? (user as Record<string, string>)
            : {};
        const answered = Object.values(userValues).some(
          (value) => !!value && value.trim() !== ''
        );
        const isCorrect =
          answered &&
          blanks.every(
            (blank) =>
              normalizeBlankText(userValues[blank.id]) === normalizeBlankText(blank.value)
          );
        items.push({ elementId: element.id, elementType: element.type, answered, isCorrect });
        break;
      }
      case 'paint-area': {
        const config = key[element.id];
        if (
          !config ||
          Array.isArray(config) ||
          !('paintSwatchId' in config || 'paintColor' in config)
        ) {
          break;
        }
        const paintConfig = config as PaintingAnswerConfig;
        const correctSwatch = (paintConfig.paintSwatchId || '').trim();
        const correctColor = (paintConfig.paintColor || '').trim().toLowerCase();
        if (!correctSwatch && !correctColor) break;
        const userColorRaw = formState[element.id];
        const userColor =
          typeof userColorRaw === 'string' ? userColorRaw.trim().toLowerCase() : '';
        const userSwatchRaw = formState[`paint-swatch:${element.id}`];
        const userSwatch =
          typeof userSwatchRaw === 'string' ? userSwatchRaw.trim() : '';
        const answered = !!userColor || !!userSwatch;
        const isCorrect =
          answered &&
          ((!!correctSwatch && userSwatch === correctSwatch) ||
            (!!correctColor && userColor === correctColor));
        items.push({ elementId: element.id, elementType: element.type, answered, isCorrect });
        break;
      }
      case 'cube-bar': {
        const config = key[element.id];
        if (!config || Array.isArray(config) || !('expectedTotal' in config)) {
          break;
        }
        const expectedTotal = (config as { expectedTotal: number }).expectedTotal;
        if (typeof expectedTotal !== 'number') break;
        const userRaw = formState[element.id];
        const answered = typeof userRaw === 'string' && userRaw !== '';
        const userTotal = answered ? Number(userRaw) : NaN;
        const isCorrect = answered && userTotal === expectedTotal;
        items.push({ elementId: element.id, elementType: element.type, answered, isCorrect });
        break;
      }
      default:
        break;
    }
  }

  // Matching is graded once as an aggregate item across all source points.
  const hasSourcePoints = elements.some(
    (el) =>
      el.type === 'matching-point' &&
      (el.data as { pointType?: string } | undefined)?.pointType === 'source'
  );
  if (hasSourcePoints) {
    const matchingConfig = findMatchingAnswerConfig(key);
    if (matchingConfig && matchingConfig.connections.length > 0) {
      const correctPairs = new Set(
        matchingConfig.connections.map((c) => `${c.sourcePointId}=>${c.targetPointId}`)
      );
      const userPairs = new Set<string>();
      Object.entries(matchingConnections).forEach(([sourceId, targets]) => {
        (targets || []).forEach((targetId) =>
          userPairs.add(`${sourceId}=>${targetId}`)
        );
      });
      const answered = userPairs.size > 0;
      const isCorrect =
        answered &&
        userPairs.size === correctPairs.size &&
        Array.from(userPairs).every((pair) => correctPairs.has(pair));
      items.push({
        elementId: 'matching-connections',
        elementType: 'matching-point',
        answered,
        isCorrect,
      });
    }
  }

  const total = items.length;
  const correct = items.filter((item) => item.isCorrect).length;
  return {
    hasGradable: total > 0,
    total,
    correct,
    allCorrect: total > 0 && correct === total,
    items,
  };
}
